/**
 * QuotaBadge Component
 *
 * Displays user's quota usage with visual progress indicator
 */

import React from 'react';
import { Box, Typography, LinearProgress, Tooltip, Chip } from '@mui/material';
import { Zap } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface QuotaBadgeProps {
  variant?: 'compact' | 'detailed';
  className?: string;
}

const QuotaBadge: React.FC<QuotaBadgeProps> = ({ variant = 'compact', className = '' }) => {
  const { quota, subscription } = useAuth();
  const { t } = useTranslation();

  if (!quota) {
    return null;
  }

  const { percentageUsed, packageStatus, hasAdvanceBalance } = quota;

  const getColor = () => {
    if (packageStatus === 'empty') return 'error';
    if (packageStatus === 'low') return 'warning';
    return 'primary';
  };

  const color = getColor();
  const statusLabel = t(`dashboard.stats.status.${packageStatus}`);
  const planName = subscription?.packageName || t('dashboard.subscription.free');

  if (variant === 'compact') {
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" display="block">
              {t('quota.badge.allowanceLabel')}: {statusLabel}
            </Typography>
            {hasAdvanceBalance && (
              <Typography variant="caption" display="block">
                {t('dashboard.stats.advanceAvailable')}
              </Typography>
            )}
            <Typography variant="caption" display="block">
              {t('quota.badge.plan', { name: planName })}
            </Typography>
          </Box>
        }
      >
        <Box
          className={`flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-1.5 dark:bg-gray-800 ${className}`}
        >
          <Zap className="h-4 w-4 text-yellow-500" />
          <Typography variant="body2" className="font-medium">
            {percentageUsed}%
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  // Detailed variant
  return (
    <Box
      className={`rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 ${className}`}
    >
      <Box className="mb-2 flex items-center justify-between">
        <Box className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-yellow-500" />
          <Typography variant="body2" className="font-semibold">
            {t('quota.badge.title')}
          </Typography>
        </Box>
        <Typography variant="caption" className="text-gray-500 dark:text-gray-400">
          {t('quota.badge.plan', { name: planName })}
        </Typography>
      </Box>

      <Box className="mb-2">
        <LinearProgress
          variant="determinate"
          value={percentageUsed}
          color={color}
          className="h-2 rounded-full"
        />
      </Box>

      <Box className="flex items-center justify-between">
        <Typography variant="body2" className="text-gray-600 dark:text-gray-400">
          {t('quota.badge.allowanceLabel')}
        </Typography>
        <Chip size="small" color={color} label={statusLabel} />
      </Box>

      {hasAdvanceBalance && (
        <Typography variant="caption" className="mt-2 block text-gray-600 dark:text-gray-400">
          {t('dashboard.stats.advanceAvailable')}
        </Typography>
      )}

      {packageStatus === 'empty' && (
        <Typography variant="caption" className="mt-2 block text-red-600 dark:text-red-400">
          {t('quota.badge.empty')}
        </Typography>
      )}
    </Box>
  );
};

export default QuotaBadge;
